import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/context/auth-context";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { SchoolInfoForm } from "@/components/auth/SchoolInfoForm";
import { PersonalInfoForm } from "@/components/auth/PersonalInfoForm";
import { PositionSelect } from "@/components/auth/PositionSelect";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { Separator } from "@/components/ui/separator";
import { createLogger } from "@/lib/utils/logger";
import { AuthLayout } from "@/components/auth/AuthLayout";

const logger = createLogger('auth', 'RegisterPage');

export default function Register() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [schoolDistrict, setSchoolDistrict] = useState("");
  const [school, setSchool] = useState("");
  const [position, setPosition] = useState("");
  const [customSchoolDistrict, setCustomSchoolDistrict] = useState("");
  const [customSchool, setCustomSchool] = useState("");
  const [customPosition, setCustomPosition] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    logger.info("Register component initialized");
  }, []);

  const handleNextStep = () => {
    if (step === 1) {
      if (!firstName || !lastName || !email || !password || !confirmPassword) {
        setError("Please fill in all fields");
        return;
      }
      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      setError(null);
      logger.info("Moving to step 2: School & Position");
      setStep(2);
    }
  };

  const handlePrevStep = () => {
    logger.info("Moving back to step 1: Personal Info");
    setStep(1);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!schoolDistrict || !school || !position) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setError(null);

    logger.info("Registration submission data: " + JSON.stringify({
      firstName,
      lastName,
      email,
      schoolDistrict,
      school,
      position,
      customSchoolDistrict: schoolDistrict === "Other" ? customSchoolDistrict : null,
      customSchool: school === "Other" ? customSchool : null,
      customPosition: position === "Other" ? customPosition : null
    }));

    try {
      await signUp(
        email,
        password,
        firstName,
        lastName,
        schoolDistrict,
        school,
        position,
        customSchoolDistrict,
        customSchool,
        customPosition
      );
      
      logger.info("Registration successful");
      
      toast({
        title: "Account created",
        description: "Your account has been successfully created.",
      });
      
      navigate("/");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred during registration";
      logger.error("Registration error: " + String(error));
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <CardHeader className="space-y-1 pb-4 relative">
        <div className="flex flex-col items-center mb-2">
              <img 
                src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
                alt="NWSISD Logo"
            className="h-20 w-auto mb-4"
              />
            </div>
        <CardTitle className="text-2xl text-center font-title text-[#004161] font-medium">
              {step === 1 ? "Create an Account" : "Complete Your Profile"}
            </CardTitle>
        <CardDescription className="text-center font-sans text-[#004161]/70">
              {step === 1 
                ? "Enter your details to create your account" 
                : "Tell us about your school and position"
              }
            </CardDescription>
          </CardHeader>
      <CardContent className="space-y-6 pb-6 relative">
            {error && (
          <Alert variant="destructive" className="bg-destructive/10 border border-destructive/20 text-destructive">
            <AlertDescription className="font-medium">{error}</AlertDescription>
              </Alert>
            )}

            {step === 1 && (
              <>
            <GoogleSignInButton />
                
            <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                <Separator className="w-full bg-[#004161]/10" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-[#004161]/60 font-serif">
                  or continue with email
                    </span>
                  </div>
                </div>
                
                <PersonalInfoForm
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  password={password}
                  confirmPassword={confirmPassword}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setEmail={setEmail}
                  setPassword={setPassword}
                  setConfirmPassword={setConfirmPassword}
                  disabled={isLoading}
                />
                
                <Button 
                  onClick={handleNextStep}
              className="w-full bg-[#004161] hover:bg-[#004161]/90 text-white transition-all duration-300 font-serif py-5 rounded-xl mt-2" 
                  disabled={!firstName || !lastName || !email || !password || !confirmPassword || isLoading}
                >
                  Continue
                </Button>
              </>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <SchoolInfoForm
                  schoolDistrict={schoolDistrict}
                  school={school}
                  customSchoolDistrict={customSchoolDistrict}
                  customSchool={customSchool}
                  setSchoolDistrict={setSchoolDistrict}
                  setSchool={setSchool}
                  setCustomSchoolDistrict={setCustomSchoolDistrict}
                  setCustomSchool={setCustomSchool}
                  disabled={isLoading}
                />
                
            <div className="space-y-1.5">
              <Label htmlFor="position" className="text-[#004161] font-serif">Position</Label>
                  <PositionSelect 
                    position={position}
                    onChange={setPosition}
                    customPosition={customPosition}
                    onCustomPositionChange={setCustomPosition}
                    disabled={isLoading}
                  />
                </div>
                
            <div className="flex gap-4 pt-2">
                  <Button 
                    type="button"
                    onClick={handlePrevStep}
                className="flex-1 bg-[#f1eceb] hover:bg-[#f1eceb]/90 text-[#004161] transition-all duration-300 font-serif py-5 rounded-xl"
                    disabled={isLoading}
                  >
                Back
                  </Button>
                  <Button 
                    type="submit" 
                className="flex-1 bg-[#004161] hover:bg-[#004161]/90 text-white transition-all duration-300 font-serif py-5 rounded-xl"
                    disabled={!schoolDistrict || !school || !position || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <span>Creating Account...</span>
                      </>
                    ) : (
                  <span>Create Account</span>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
      <CardFooter className="flex flex-col space-y-4 pt-2 pb-6 relative">
        <p className="text-sm text-center text-[#004161]/70 font-serif">
              Already have an account?{" "}
          <Link to="/auth/login" className="text-[#0f8f8c] hover:text-[#0f8f8c]/80 transition-all duration-300">
                Sign in
              </Link>
            </p>
        
        <div className="flex justify-center gap-6 text-xs text-[#004161]/50 font-serif">
          <Link to="/privacy-policy" className="hover:text-[#0f8f8c] transition-all duration-300">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="hover:text-[#0f8f8c] transition-all duration-300">
            Terms of Service
          </Link>
        </div>
          </CardFooter>
    </AuthLayout>
  );
}
